import React from 'react';
import { useUserStatus } from '../../contexts/UserStatusContext';

const UpgradeModal = ({ isOpen, onClose, trigger = 'create' }) => {
  const { remainingLinks, userStatus } = useUserStatus();

  const handleClose = () => {
    onClose();
  };

  // Don't render if user has remaining links or is pro
  if (remainingLinks > 0 || userStatus === 'pro') return null;

  if (!isOpen) return null;

  // Dynamic content based on trigger
  const getContent = () => {
    switch (trigger) {
      case 'plus':
        return {
          emoji: '🚀',
          title: 'Ready to level up?',
          subtitle: 'You\'ve used all your free links this month',
          description: 'Upgrade to Pro and create unlimited trackable links. Perfect for power users who share lots of AI conversations.'
        };
      case 'create':
        return {
          emoji: '✨',
          title: 'Oops, you\'re all out!',
          subtitle: 'No more free links this month',
          description: 'Don\'t worry though – upgrading to Pro gives you unlimited link generations plus exclusive features.'
        };
      case 'button':
        return {
          emoji: '🎯',
          title: 'Hit your limit?',
          subtitle: 'Time to go Pro',
          description: 'You\'ve maxed out your free links for this month. Upgrade now to keep sharing your AI conversations without limits.'
        };
      default:
        return {
          emoji: '⚡',
          title: 'Weekly limit reached',
          subtitle: 'Ready for unlimited?',
          description: 'Upgrade to Pro for unlimited link generations, advanced analytics, and priority support.'
        };
    }
  };

  const content = getContent();

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          zIndex: 1000
        }}
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          width: '100%',
          maxWidth: '400px',
          padding: '0 20px'
        }}
      >
        {/* Modal Content */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            padding: '32px 24px 28px 24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            position: 'relative',
            width: '100%'
          }}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#f5f5f7',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              color: '#8e8e93'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e5e5ea';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f5f5f7';
              e.target.style.transform = 'scale(1)';
            }}
          >
            ✕
          </button>

          {/* Emoji */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div
              style={{
                fontSize: '48px',
                lineHeight: '1'
              }}
            >
              {content.emoji}
            </div>
          </div>

          {/* Title */}
          <h2
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1d1d1f',
              textAlign: 'center',
              marginBottom: '8px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              lineHeight: '1.2'
            }}
          >
            {content.title}
          </h2>

          {/* Subtitle */}
          <p
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#86868b',
              textAlign: 'center',
              marginBottom: '20px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
            }}
          >
            {content.subtitle}
          </p>

          {/* Description */}
          <p
            style={{
              fontSize: '15px',
              fontWeight: '400',
              color: '#515154',
              textAlign: 'center',
              lineHeight: '1.5',
              marginBottom: '32px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
            }}
          >
            {content.description}
          </p>

          {/* Pro Features */}
          <div
            style={{
              backgroundColor: '#f5f5f7',
              borderRadius: '16px',
              padding: '18px 20px',
              marginBottom: '24px'
            }}
          >
            <h3
              style={{
                fontSize: '17px',
                fontWeight: '600',
                color: '#1d1d1f',
                marginBottom: '10px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
              }}
            >
              ✨ What you get with Pro:
            </h3>
            
            {[
              { icon: '🚀', text: 'Unlimited links' },
              { icon: '📊', text: 'Additional data analytics' },
              { icon: '⚡', text: 'Priority support' }
            ].map((feature, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: index === 2 ? '0' : '6px',
                  fontSize: '15px',
                  color: '#515154',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
                }}
              >
                <span style={{ marginRight: '12px', fontSize: '16px' }}>{feature.icon}</span>
                {feature.text}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Primary CTA */}
            <button
              onClick={() => {
                // TODO: Navigate to upgrade/payment page
                console.log('Navigate to Pro upgrade');
                handleClose();
              }}
              style={{
                width: '100%',
                padding: '16px 24px',
                backgroundColor: '#007AFF',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0056b3';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#007AFF';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
              }}
            >
              Upgrade to Pro
            </button>

            {/* Secondary CTA */}
            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: '#007AFF',
                border: '1px solid #e5e5ea',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f7';
                e.target.style.borderColor = '#d1d1d6';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#e5e5ea';
              }}
            >
              Maybe later
            </button>
          </div>

          {/* Small print */}
          <p
            style={{
              fontSize: '12px',
              color: '#8e8e93',
              textAlign: 'center',
              marginTop: '16px',
              lineHeight: '1.4',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }}
          >
            Links reset weekly. Cancel anytime.
          </p>
        </div>
      </div>

    </>
  );
};

export default UpgradeModal;